import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Users, TrendingUp, Mail, Upload, Search, ArrowLeft, Phone, MapPin, Building2,
  Calendar, AlertTriangle, CheckCircle2, XCircle, Send, Settings, Plus, Trash2, Edit3,
  RefreshCw, Briefcase, Inbox, FileSpreadsheet, Eye, Loader2
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLOR_MAP = {
  verde: { bg: "bg-green-100", text: "text-green-700", border: "border-green-300", dot: "bg-green-500" },
  giallo: { bg: "bg-yellow-100", text: "text-yellow-700", border: "border-yellow-300", dot: "bg-yellow-500" },
  arancione: { bg: "bg-orange-100", text: "text-orange-700", border: "border-orange-300", dot: "bg-orange-500" },
  rosso: { bg: "bg-red-100", text: "text-red-700", border: "border-red-300", dot: "bg-red-500" },
  rosso_lampeggiante: { bg: "bg-red-200", text: "text-red-900 font-bold", border: "border-red-500", dot: "bg-red-600 animate-pulse" },
  grigio: { bg: "bg-gray-100", text: "text-gray-500", border: "border-gray-200", dot: "bg-gray-400" },
};

const COLOR_LABEL = {
  verde: "0-30g",
  giallo: "31-60g",
  arancione: "61-90g",
  rosso: "91-180g",
  rosso_lampeggiante: ">180g 🔥",
  grigio: "Mai ordinato",
};

const KpiCard = ({ title, value, icon: Icon, color = "rose", testId }) => (
  <div data-testid={testId} className={`bg-white rounded-2xl border border-rose-100 p-5 hover:shadow-md transition`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs text-gray-500 uppercase tracking-wider">{title}</p>
        <p className={`text-3xl font-bold mt-1 text-${color}-700`}>{value}</p>
      </div>
      <Icon className={`w-8 h-8 text-${color}-400`} />
    </div>
  </div>
);

const fetchAuth = (url, opts = {}) => {
  const token = localStorage.getItem("token");
  return fetch(`${API}${url}`, {
    ...opts,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
  });
};

// ============================================================
// DASHBOARD CRM (admin/direzione vede tutto, agente vede solo i suoi)
// ============================================================
export function CrmDashboardPage() {
  const { user } = useAuth();
  const isAdmin = ["admin", "direzione", "docente"].includes(user?.role);
  const [data, setData] = useState(null);
  const [adminStats, setAdminStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchAuth("/crm/dashboard/agente");
      if (r.ok) setData(await r.json());
      if (isAdmin) {
        const r2 = await fetchAuth("/crm/dashboard/admin");
        if (r2.ok) setAdminStats(await r2.json());
      }
    } catch (e) { toast.error("Errore caricamento dashboard"); }
    setLoading(false);
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-8 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin text-rose-400" /></div>;
  if (!data) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-amber-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link to="/admin" data-testid="crm-back-dashboard" className="inline-flex items-center gap-1 text-sm text-[#7A4856] hover:underline mb-2">
              <ArrowLeft className="w-4 h-4" /> Torna alla Dashboard Admin
            </Link>
            <h1 className="text-3xl font-bold text-[#5A3A3A]">CRM ITA318 · {isAdmin ? "Direzione" : "Agente"}</h1>
            <p className="text-sm text-gray-500 mt-1">Ciao {user?.name || user?.email} — {isAdmin ? "vista globale" : "vedi solo i tuoi clienti"}</p>
          </div>
          <div className="flex gap-2">
            <Link to="/admin/crm/clienti" data-testid="crm-link-clienti" className="px-4 py-2 bg-[#B87A8A] text-white rounded-full text-sm font-medium hover:bg-[#a06978]">
              <Users className="w-4 h-4 inline mr-1" /> Clienti
            </Link>
            {isAdmin && <>
              <Link to="/admin/crm/ordini" data-testid="crm-link-ordini" className="px-4 py-2 bg-white border border-rose-200 text-[#7A4856] rounded-full text-sm font-medium hover:bg-rose-50">
                <Inbox className="w-4 h-4 inline mr-1" /> Ordini in attesa
              </Link>
              <Link to="/admin/crm/promemoria" data-testid="crm-link-promemoria" className="px-4 py-2 bg-white border border-amber-300 text-amber-700 rounded-full text-sm font-medium hover:bg-amber-50">
                🔔 Promemoria Visite
              </Link>
              <Link to="/admin/crm/invio-mirato" data-testid="crm-link-invio-mirato" className="px-4 py-2 bg-gradient-to-r from-rose-500 to-rose-700 text-white rounded-full text-sm font-medium hover:opacity-90 shadow">
                📨 Invio mirato
              </Link>
              <Link to="/admin/crm/import" data-testid="crm-link-import" className="px-4 py-2 bg-white border border-rose-200 text-[#7A4856] rounded-full text-sm font-medium hover:bg-rose-50">
                <Upload className="w-4 h-4 inline mr-1" /> Import Excel
              </Link>
              <Link to="/admin/crm/outlook" data-testid="crm-link-outlook" className="px-4 py-2 bg-white border border-rose-200 text-[#7A4856] rounded-full text-sm font-medium hover:bg-rose-50">
                <Mail className="w-4 h-4 inline mr-1" /> Outlook
              </Link>
            </>}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <KpiCard title="Totale Clienti" value={data.totale_clienti} icon={Users} testId="kpi-totale-clienti" />
          <KpiCard title="Ordini Oggi" value={data.ordini_oggi.length} icon={CheckCircle2} color="green" testId="kpi-ordini-oggi" />
          <KpiCard title="Da richiamare ≥60g" value={data.da_richiamare_60g.length} icon={Phone} color="yellow" testId="kpi-richiamare-60" />
          <KpiCard title="Da richiamare ≥90g" value={data.da_richiamare_90g.length} icon={AlertTriangle} color="orange" testId="kpi-richiamare-90" />
          <KpiCard title="ROSSO LAMPEGGIANTE" value={data.rosso_lampeggiante.length} icon={AlertTriangle} color="red" testId="kpi-rosso-lampeggiante" />
        </div>

        {isAdmin && adminStats && (
          <div className="bg-white rounded-2xl border border-rose-100 p-5 mb-6">
            <h2 className="text-lg font-semibold mb-3 text-[#5A3A3A]">Vendite per Agente</h2>
            <div className="space-y-2">
              {(adminStats.vendite_per_agente || []).map((v, i) => (
                <div key={i} className="flex items-center justify-between border-b border-rose-50 pb-2">
                  <div className="text-sm text-gray-700">{v._id || "(nessun agente)"}</div>
                  <div className="text-sm text-gray-500">{v.ordini} ordini</div>
                  <div className="text-base font-semibold text-[#7A4856]">€ {Number(v.fatturato || 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}</div>
                </div>
              ))}
              {(adminStats.vendite_per_agente || []).length === 0 && <div className="text-sm text-gray-400">Nessun dato ancora.</div>}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ClientiBucket title="🔥 Rosso Lampeggiante (>180g)" clienti={data.rosso_lampeggiante} testIdPrefix="bucket-rosso-lamp" />
          <ClientiBucket title="🟠 Da richiamare (>90g)" clienti={data.da_richiamare_90g.filter(c => (c.giorni_inattivo || 0) < 180)} testIdPrefix="bucket-90" />
          <ClientiBucket title="🟡 Attenzione (60-90g)" clienti={data.da_richiamare_60g.filter(c => (c.giorni_inattivo || 0) < 90)} testIdPrefix="bucket-60" />
          <ClientiBucket title="🏆 Top 10 Clienti per Fatturato" clienti={data.top_clienti} showFatturato testIdPrefix="bucket-top" />
        </div>
      </div>
    </div>
  );
}

function ClientiBucket({ title, clienti, showFatturato, testIdPrefix }) {
  return (
    <div className="bg-white rounded-2xl border border-rose-100 p-5">
      <h3 className="text-base font-semibold text-[#5A3A3A] mb-3">{title} <span className="text-sm text-gray-400 font-normal">({clienti.length})</span></h3>
      <div className="space-y-2 max-h-80 overflow-y-auto">
        {clienti.slice(0, 30).map((c) => {
          const cm = COLOR_MAP[c.colore] || COLOR_MAP.grigio;
          return (
            <Link key={c.cliente_id} to={`/admin/crm/clienti/${c.cliente_id}`} data-testid={`${testIdPrefix}-${c.cliente_id}`}
              className={`flex items-center justify-between p-2 rounded-lg border ${cm.border} ${cm.bg} hover:shadow-sm transition`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-gray-800 truncate">{c.ragione_sociale}</div>
                <div className="text-xs text-gray-500">{c.citta || "—"} · {c.codice_cliente || "no-cod"}</div>
              </div>
              <div className="text-right">
                {showFatturato
                  ? <div className="text-sm font-semibold text-[#7A4856]">€ {Number(c.fatturato_totale || 0).toLocaleString("it-IT")}</div>
                  : <div className={`text-xs ${cm.text}`}>{c.giorni_inattivo !== null ? `${c.giorni_inattivo}g` : "—"}</div>
                }
              </div>
            </Link>
          );
        })}
        {clienti.length === 0 && <div className="text-sm text-gray-400 text-center py-4">Nessun cliente</div>}
      </div>
    </div>
  );
}


// ============================================================
// LISTA CLIENTI
// ============================================================
export function CrmClientiPage() {
  const { user } = useAuth();
  const isAdmin = ["admin", "direzione"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const q = search ? `?search=${encodeURIComponent(search)}` : "";
    const r = await fetchAuth(`/crm/clienti${q}`);
    if (r.ok) {
      const d = await r.json();
      setItems(d.items);
    }
    setLoading(false);
  }, [search]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-amber-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Link to="/admin/crm" className="text-[#7A4856] hover:underline text-sm" data-testid="back-to-crm-dashboard">
              <ArrowLeft className="w-4 h-4 inline" /> Dashboard
            </Link>
            <h1 className="text-2xl font-bold text-[#5A3A3A]">Clienti CRM</h1>
          </div>
          {isAdmin && (
            <button onClick={() => setShowCreate(true)} data-testid="btn-new-cliente" className="px-4 py-2 bg-[#B87A8A] text-white rounded-full text-sm font-medium hover:bg-[#a06978]">
              <Plus className="w-4 h-4 inline mr-1" /> Nuovo cliente
            </button>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-rose-100 p-4 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input data-testid="search-clienti" type="text" placeholder="Cerca per ragione sociale, codice, partita iva, città..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-rose-200 rounded-full text-sm focus:outline-none focus:border-[#B87A8A]" />
          </div>
        </div>

        {loading ? <div className="text-center py-8"><Loader2 className="w-6 h-6 mx-auto animate-spin text-rose-400" /></div> : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((c) => {
              const cm = COLOR_MAP[c.colore] || COLOR_MAP.grigio;
              return (
                <Link key={c.cliente_id} to={`/admin/crm/clienti/${c.cliente_id}`} data-testid={`cliente-card-${c.cliente_id}`}
                  className={`bg-white rounded-xl border ${cm.border} p-4 hover:shadow-md transition relative`}>
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-gray-800 pr-12 truncate">{c.ragione_sociale}</h3>
                    <span className={`absolute top-3 right-3 px-2 py-0.5 rounded-full text-xs ${cm.bg} ${cm.text} flex items-center gap-1`}>
                      <span className={`w-2 h-2 rounded-full ${cm.dot}`}></span>
                      {c.giorni_inattivo !== null ? `${c.giorni_inattivo}g` : "—"}
                    </span>
                  </div>
                  {c.codice_cliente && <p className="text-xs text-gray-500">Cod: {c.codice_cliente}</p>}
                  {c.citta && <p className="text-xs text-gray-500"><MapPin className="w-3 h-3 inline" /> {c.citta} {c.provincia ? `(${c.provincia})` : ""}</p>}
                  {c.telefono && <p className="text-xs text-gray-500"><Phone className="w-3 h-3 inline" /> {c.telefono}</p>}
                  {c.agente_email && <p className="text-xs text-gray-400 mt-2">Agente: {c.agente_email}</p>}
                  {c.promemoria_inviato && (
                    <div className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300"
                         title={c.promemoria_ultimo_invio ? `Ultimo invio: ${new Date(c.promemoria_ultimo_invio).toLocaleString("it-IT")}` : ""}>
                      <Mail className="w-2.5 h-2.5" /> Promemoria inviato
                    </div>
                  )}
                  {c.ultimo_ordine_data && (
                    <div className="mt-2 pt-2 border-t border-rose-50 text-xs">
                      Ultimo ordine: <span className="font-semibold">{c.ultimo_ordine_data}</span> · € {Number(c.ultimo_ordine_importo || 0).toLocaleString("it-IT")}
                    </div>
                  )}
                </Link>
              );
            })}
            {items.length === 0 && <div className="col-span-full text-center py-8 text-gray-400">Nessun cliente</div>}
          </div>
        )}
      </div>

      {showCreate && <CreateClienteModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}


function CreateClienteModal({ onClose, onSaved, initial = {} }) {
  const [form, setForm] = useState({
    ragione_sociale: "", codice_cliente: "", indirizzo: "", cap: "", citta: "", provincia: "", regione: "",
    telefono: "", cellulare: "", email: "", pec: "", partita_iva: "", codice_fiscale: "", agente_email: "", tipo: "diretto", note: "",
    ...initial,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.ragione_sociale.trim()) { toast.error("Ragione sociale obbligatoria"); return; }
    setSaving(true);
    const method = initial.cliente_id ? "PUT" : "POST";
    const url = initial.cliente_id ? `/crm/clienti/${initial.cliente_id}` : "/crm/clienti";
    const r = await fetchAuth(url, { method, body: JSON.stringify(form) });
    setSaving(false);
    if (r.ok) { toast.success("Salvato"); onSaved(); }
    else { const e = await r.json().catch(() => ({})); toast.error(e.detail || "Errore"); }
  };

  const F = (label, key, type = "text") => (
    <div>
      <label className="text-xs text-gray-500">{label}</label>
      <input data-testid={`cliente-field-${key}`} type={type} value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="w-full px-3 py-2 border border-rose-200 rounded-lg text-sm focus:outline-none focus:border-[#B87A8A]" />
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-2xl w-full p-6 my-8" data-testid="cliente-modal">
        <h2 className="text-xl font-bold mb-4 text-[#5A3A3A]">{initial.cliente_id ? "Modifica cliente" : "Nuovo cliente"}</h2>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">{F("Ragione Sociale *", "ragione_sociale")}</div>
          {F("Codice Cliente", "codice_cliente")}
          {F("Partita IVA", "partita_iva")}
          <div className="col-span-2">{F("Indirizzo", "indirizzo")}</div>
          {F("CAP", "cap")}
          {F("Città", "citta")}
          {F("Provincia", "provincia")}
          {F("Regione", "regione")}
          {F("Telefono", "telefono")}
          {F("Cellulare", "cellulare")}
          {F("Email", "email", "email")}
          {F("PEC", "pec")}
          {F("Agente assegnato (email)", "agente_email")}
          <div>
            <label className="text-xs text-gray-500">Tipo</label>
            <select data-testid="cliente-field-tipo" value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} className="w-full px-3 py-2 border border-rose-200 rounded-lg text-sm">
              <option value="diretto">Diretto</option>
              <option value="distributore">Distributore</option>
              <option value="sub-cliente_distributore">Sub-cliente Distributore</option>
              <option value="da_verificare">Da verificare</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-xs text-gray-500">Note</label>
            <textarea data-testid="cliente-field-note" value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} rows="3"
              className="w-full px-3 py-2 border border-rose-200 rounded-lg text-sm" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-full text-sm" data-testid="cliente-cancel">Annulla</button>
          <button onClick={save} disabled={saving} data-testid="cliente-save" className="px-4 py-2 bg-[#B87A8A] text-white rounded-full text-sm font-medium hover:bg-[#a06978] disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 inline animate-spin" /> : "Salva"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ============================================================
// DETTAGLIO CLIENTE (scheda con ultimo ordine, colori, contatti)
// ============================================================
export function CrmClienteDetailPage() {
  const { cliente_id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = ["admin", "direzione"].includes(user?.role);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [newContatto, setNewContatto] = useState({ tipo: "telefonata", note: "" });

  const load = useCallback(async () => {
    setLoading(true);
    const r = await fetchAuth(`/crm/clienti/${cliente_id}`);
    if (r.ok) setData(await r.json());
    else { toast.error("Cliente non trovato o non accessibile"); navigate("/admin/crm/clienti"); }
    setLoading(false);
  }, [cliente_id, navigate]);

  useEffect(() => { load(); }, [load]);

  const addContatto = async () => {
    if (!newContatto.note.trim()) { toast.error("Inserisci una nota"); return; }
    const r = await fetchAuth(`/crm/clienti/${cliente_id}/contatti`, { method: "POST", body: JSON.stringify(newContatto) });
    if (r.ok) { toast.success("Contatto registrato"); setNewContatto({ tipo: "telefonata", note: "" }); load(); }
    else toast.error("Errore");
  };

  const handleDelete = async () => {
    if (!window.confirm("Eliminare definitivamente questo cliente?")) return;
    const r = await fetchAuth(`/crm/clienti/${cliente_id}`, { method: "DELETE" });
    if (r.ok) { toast.success("Eliminato"); navigate("/admin/crm/clienti"); }
    else toast.error("Errore");
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin text-rose-400" /></div>;
  if (!data) return null;

  const c = data.cliente;
  const cm = COLOR_MAP[c.colore] || COLOR_MAP.grigio;

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-amber-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <Link to="/admin/crm/clienti" className="text-[#7A4856] hover:underline text-sm">
            <ArrowLeft className="w-4 h-4 inline" /> Lista clienti
          </Link>
        </div>

        {/* Header con colore prominente */}
        <div className={`rounded-2xl border-2 ${cm.border} ${cm.bg} p-6 mb-4`} data-testid="cliente-header">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-800">{c.ragione_sociale}</h1>
              <div className="flex flex-wrap gap-2 mt-2 text-sm text-gray-600">
                {c.codice_cliente && <span>Codice: <b>{c.codice_cliente}</b></span>}
                {c.partita_iva && <span>P.IVA: {c.partita_iva}</span>}
                {c.tipo && <span className="px-2 py-0.5 bg-white/60 rounded-full text-xs uppercase">{c.tipo}</span>}
                {(data.promemoria || []).length > 0 && (
                  <span
                    data-testid="badge-promemoria-inviato"
                    className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300"
                    title={`Ultimo invio: ${new Date(data.promemoria[0].created_at).toLocaleString("it-IT")}`}
                  >
                    <Mail className="w-3 h-3" />
                    Promemoria inviato
                    {data.promemoria.length > 1 && <span className="opacity-70">×{data.promemoria.length}</span>}
                  </span>
                )}
              </div>
            </div>
            {isAdmin && (
              <div className="flex gap-2">
                <button onClick={() => setShowEdit(true)} className="p-2 bg-white/80 rounded-full hover:bg-white" data-testid="btn-edit-cliente"><Edit3 className="w-4 h-4 text-[#7A4856]" /></button>
                <button onClick={handleDelete} className="p-2 bg-white/80 rounded-full hover:bg-red-50" data-testid="btn-delete-cliente"><Trash2 className="w-4 h-4 text-red-500" /></button>
              </div>
            )}
          </div>

          {/* Big "Ultimo Ordine" */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="bg-white/80 rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase">Ultimo Ordine</p>
              <p className="text-2xl font-bold text-gray-800" data-testid="ultimo-ordine-data">{c.ultimo_ordine_data || "Mai ordinato"}</p>
              {c.ultimo_ordine_numero && <p className="text-xs text-gray-500">N° {c.ultimo_ordine_numero}</p>}
            </div>
            <div className={`rounded-xl p-4 ${cm.bg} border ${cm.border}`}>
              <p className="text-xs text-gray-500 uppercase">Giorni Trascorsi</p>
              <p className={`text-3xl font-bold ${cm.text}`} data-testid="giorni-inattivo">
                <span className={`inline-block w-3 h-3 rounded-full mr-2 ${cm.dot}`}></span>
                {c.giorni_inattivo !== null ? `${c.giorni_inattivo} giorni` : "—"}
              </p>
              <p className="text-xs text-gray-500 mt-1">{COLOR_LABEL[c.colore]}</p>
            </div>
            <div className="bg-white/80 rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase">Importo Ultimo / Totale</p>
              <p className="text-2xl font-bold text-[#7A4856]">€ {Number(c.ultimo_ordine_importo || 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}</p>
              <p className="text-xs text-gray-500">Tot: € {Number(c.fatturato_totale || 0).toLocaleString("it-IT")} ({c.ordini_count || 0} ordini)</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Dati anagrafici */}
          <div className="bg-white rounded-2xl border border-rose-100 p-5">
            <h3 className="text-base font-semibold mb-3 text-[#5A3A3A]">Dati Anagrafici</h3>
            <div className="space-y-2 text-sm">
              {c.indirizzo && <p><MapPin className="w-3 h-3 inline mr-1 text-gray-400" />{c.indirizzo}</p>}
              {(c.cap || c.citta) && <p className="text-gray-600">{c.cap} {c.citta} {c.provincia ? `(${c.provincia})` : ""}</p>}
              {c.telefono && <p><Phone className="w-3 h-3 inline mr-1 text-gray-400" />{c.telefono}</p>}
              {c.cellulare && <p><Phone className="w-3 h-3 inline mr-1 text-gray-400" />Cell: {c.cellulare}</p>}
              {c.email && <p><Mail className="w-3 h-3 inline mr-1 text-gray-400" />{c.email}</p>}
              {c.pec && <p className="text-gray-500 text-xs">PEC: {c.pec}</p>}
              {c.agente_email && <p className="pt-2 border-t border-rose-50 text-xs"><Briefcase className="w-3 h-3 inline mr-1" />Agente: {c.agente_email}</p>}
              {c.note && <p className="mt-2 text-xs text-gray-500 italic">{c.note}</p>}
            </div>
          </div>

          {/* Storico ordini */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-rose-100 p-5">
            <h3 className="text-base font-semibold mb-3 text-[#5A3A3A]">Storico Ordini ({data.ordini.length})</h3>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {data.ordini.map((o) => (
                <div key={o.ordine_id} data-testid={`ordine-${o.ordine_id}`} className="border border-rose-100 rounded-lg p-3 hover:bg-rose-50/40 transition">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-gray-800">Ordine #{o.numero_ordine}</div>
                      <div className="text-xs text-gray-500">{o.data_ordine} · {o.fonte}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-[#7A4856]">€ {Number(o.importo_totale || 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}</div>
                      <div className="text-xs text-gray-500">{(o.items || []).length} prodotti</div>
                    </div>
                  </div>
                  {(o.items || []).length > 0 && (
                    <div className="mt-2 text-xs text-gray-600 grid grid-cols-2 gap-1">
                      {(o.items || []).slice(0, 8).map((it, i) => (
                        <div key={i}>{it.codice || it.descrizione} <span className="text-gray-400">×{it.quantita}</span></div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {data.ordini.length === 0 && <div className="text-center text-gray-400 py-4 text-sm">Nessun ordine</div>}
            </div>
          </div>
        </div>

        {/* Contatti log */}
        <div className="bg-white rounded-2xl border border-rose-100 p-5 mt-4">
          <h3 className="text-base font-semibold mb-3 text-[#5A3A3A]">Cronologia Contatti</h3>
          <div className="flex gap-2 mb-3">
            <select value={newContatto.tipo} onChange={(e) => setNewContatto({ ...newContatto, tipo: e.target.value })}
              className="px-3 py-2 border border-rose-200 rounded-lg text-sm" data-testid="new-contatto-tipo">
              <option value="telefonata">📞 Telefonata</option>
              <option value="visita">🚶 Visita</option>
              <option value="email">✉️ Email</option>
              <option value="nota">📝 Nota</option>
            </select>
            <input value={newContatto.note} onChange={(e) => setNewContatto({ ...newContatto, note: e.target.value })}
              placeholder="Nota..." className="flex-1 px-3 py-2 border border-rose-200 rounded-lg text-sm" data-testid="new-contatto-note" />
            <button onClick={addContatto} className="px-4 py-2 bg-[#B87A8A] text-white rounded-lg text-sm font-medium hover:bg-[#a06978]" data-testid="btn-add-contatto">
              <Plus className="w-4 h-4 inline mr-1" /> Aggiungi
            </button>
          </div>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {data.contatti.map((l) => (
              <div key={l.log_id} className="flex items-start gap-2 p-2 border-b border-rose-50 text-sm">
                <span className="text-xs text-gray-400 w-32 shrink-0">{(l.data || "").slice(0, 16).replace("T", " ")}</span>
                <span className="px-2 py-0.5 bg-rose-50 text-rose-700 rounded text-xs uppercase">{l.tipo}</span>
                <span className="flex-1 text-gray-700">{l.note}</span>
              </div>
            ))}
            {data.contatti.length === 0 && <div className="text-xs text-gray-400 text-center py-2">Nessun contatto registrato</div>}
          </div>
        </div>

        {/* Promemoria Visite Inviati */}
        {(data.promemoria || []).length > 0 && (
          <div className="bg-white rounded-2xl border border-emerald-200 p-5 mt-4" data-testid="promemoria-section">
            <h3 className="text-base font-semibold mb-3 text-[#5A3A3A] flex items-center gap-2">
              <Mail className="w-4 h-4 text-emerald-600" /> Promemoria Visite Inviati ({data.promemoria.length})
            </h3>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {data.promemoria.map((p) => {
                const fbLabel = p.feedback === "visited" ? "✅ Visitato" :
                                p.feedback === "will_visit" ? "📅 Visita programmata" :
                                p.feedback ? p.feedback : "In attesa di feedback";
                const fbColor = p.feedback === "visited" ? "bg-green-100 text-green-700" :
                                p.feedback === "will_visit" ? "bg-blue-100 text-blue-700" :
                                "bg-amber-50 text-amber-700";
                return (
                  <div key={p.token} className="flex flex-wrap items-center gap-2 p-2 border border-emerald-50 rounded-lg text-sm bg-emerald-50/30">
                    <span className="text-xs text-gray-500 w-36 shrink-0">
                      {new Date(p.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </span>
                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded text-xs">
                      📧 {p.destinatario}
                    </span>
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
                      {p.giorni} gg
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs ${fbColor}`}>
                      {fbLabel}
                      {p.feedback_at && ` · ${new Date(p.feedback_at).toLocaleDateString("it-IT")}`}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {showEdit && <CreateClienteModal initial={c} onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); load(); }} />}
    </div>
  );
}


// ============================================================
// ORDINI IN ATTESA DI VERIFICA
// ============================================================
export function CrmOrdiniInboxPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(null);
  const [clientiOptions, setClientiOptions] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await fetchAuth("/crm/ordini?stato=in_attesa");
    if (r.ok) setItems((await r.json()).items || []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadClientiForAssign = async (q) => {
    const r = await fetchAuth(`/crm/clienti?search=${encodeURIComponent(q)}`);
    if (r.ok) setClientiOptions((await r.json()).items.slice(0, 20));
  };

  const approva = async (ordine_id, cliente_id) => {
    const url = cliente_id ? `/crm/ordini/${ordine_id}/approva?cliente_id=${cliente_id}` : `/crm/ordini/${ordine_id}/approva`;
    const r = await fetchAuth(url, { method: "POST" });
    if (r.ok) { toast.success("Ordine approvato"); setAssigning(null); load(); }
    else toast.error("Errore");
  };

  const respingi = async (ordine_id) => {
    const motivo = window.prompt("Motivo del rifiuto?");
    if (motivo === null) return;
    const r = await fetchAuth(`/crm/ordini/${ordine_id}/respingi?motivo=${encodeURIComponent(motivo)}`, { method: "POST" });
    if (r.ok) { toast.success("Respinto"); load(); }
  };

  const syncOutlook = async () => {
    toast.info("Sync Outlook in corso...");
    const r = await fetchAuth("/crm/outlook/sync?limit=100", { method: "POST" });
    if (r.ok) {
      const d = await r.json();
      toast.success(`${d.inserted} nuovi ordini · ${d.skipped_duplicates} duplicati`);
      load();
    } else { const e = await r.json().catch(() => ({})); toast.error(e.detail || "Sync fallita"); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-amber-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Link to="/admin/crm" className="text-[#7A4856] hover:underline text-sm"><ArrowLeft className="w-4 h-4 inline" /> Dashboard</Link>
            <h1 className="text-2xl font-bold text-[#5A3A3A]">Ordini in attesa di verifica</h1>
          </div>
          <button onClick={syncOutlook} data-testid="btn-sync-outlook" className="px-4 py-2 bg-[#B87A8A] text-white rounded-full text-sm font-medium hover:bg-[#a06978]">
            <RefreshCw className="w-4 h-4 inline mr-1" /> Sync Outlook ora
          </button>
        </div>

        {loading ? <div className="text-center py-8"><Loader2 className="w-6 h-6 mx-auto animate-spin text-rose-400" /></div> : (
          <div className="space-y-3">
            {items.map((o) => (
              <div key={o.ordine_id} data-testid={`inbox-ordine-${o.ordine_id}`} className="bg-white rounded-xl border border-amber-200 p-4 hover:shadow-md transition">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-lg">Ordine #{o.numero_ordine}</span>
                      <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full uppercase">{o.fonte}</span>
                    </div>
                    <div className="text-sm text-gray-600">{o.data_ordine} · <span className="font-semibold">€ {Number(o.importo_totale || 0).toLocaleString("it-IT")}</span></div>
                    {o.cliente_ragione_sociale && <div className="text-sm">Cliente proposto: <b>{o.cliente_ragione_sociale}</b></div>}
                    {o.sender_email && <div className="text-xs text-gray-500">Mittente: {o.sender_email} ({o.sender_type})</div>}
                    {(o.items || []).length > 0 && (
                      <div className="mt-2 text-xs text-gray-600">
                        {(o.items || []).map((it, i) => <span key={i} className="inline-block mr-3">{it.codice} ×{it.quantita}</span>)}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    {assigning === o.ordine_id ? (
                      <div className="bg-rose-50 p-3 rounded-lg w-64">
                        <input placeholder="Cerca cliente..." onChange={(e) => loadClientiForAssign(e.target.value)}
                          className="w-full px-2 py-1 border rounded text-sm mb-2" data-testid={`assign-search-${o.ordine_id}`} />
                        <div className="max-h-40 overflow-y-auto space-y-1">
                          {clientiOptions.map((c) => (
                            <button key={c.cliente_id} onClick={() => approva(o.ordine_id, c.cliente_id)}
                              className="w-full text-left px-2 py-1 bg-white hover:bg-rose-100 rounded text-xs">
                              {c.ragione_sociale} <span className="text-gray-400">— {c.citta}</span>
                            </button>
                          ))}
                        </div>
                        <button onClick={() => setAssigning(null)} className="text-xs text-gray-500 mt-1">Annulla</button>
                      </div>
                    ) : (
                      <>
                        <button onClick={() => approva(o.ordine_id, o.cliente_id)} disabled={!o.cliente_id}
                          data-testid={`btn-approve-${o.ordine_id}`}
                          className="px-3 py-1.5 bg-green-600 text-white rounded-full text-xs font-medium hover:bg-green-700 disabled:opacity-30">
                          <CheckCircle2 className="w-3 h-3 inline mr-1" /> Approva
                        </button>
                        <button onClick={() => { setAssigning(o.ordine_id); setClientiOptions([]); }}
                          data-testid={`btn-assign-${o.ordine_id}`}
                          className="px-3 py-1.5 bg-white border border-rose-200 text-[#7A4856] rounded-full text-xs font-medium hover:bg-rose-50">
                          <Users className="w-3 h-3 inline mr-1" /> Assegna cliente
                        </button>
                        <button onClick={() => respingi(o.ordine_id)}
                          data-testid={`btn-reject-${o.ordine_id}`}
                          className="px-3 py-1.5 bg-white border border-red-200 text-red-600 rounded-full text-xs hover:bg-red-50">
                          <XCircle className="w-3 h-3 inline mr-1" /> Respingi
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {items.length === 0 && <div className="text-center py-8 text-gray-400">Nessun ordine in attesa.</div>}
          </div>
        )}
      </div>
    </div>
  );
}


// ============================================================
// IMPORT EXCEL
// ============================================================
export function CrmImportPage() {
  const [tab, setTab] = useState("clienti");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [seedingWoo, setSeedingWoo] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [wipeExisting, setWipeExisting] = useState(false);

  const upload = async () => {
    if (!file) { toast.error("Seleziona un file"); return; }
    if (tab === "elenco-ddt" && wipeExisting) {
      if (!window.confirm("⚠️ Sei sicuro? Verranno CANCELLATI tutti gli ordini importati precedentemente dal file 'Elenco DDT' (non tocca gli ordini email WooCommerce né quelli manuali).")) return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    const token = localStorage.getItem("token");
    const endpoint = tab === "elenco-ddt" ? "elenco-ddt" : tab;
    const qs = (tab === "elenco-ddt" && wipeExisting) ? "?wipe_existing=true" : "";
    const r = await fetch(`${API}/crm/import/${endpoint}${qs}`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
    setUploading(false);
    if (r.ok) {
      const d = await r.json();
      setResult(d);
      toast.success(`Import completato: ${d.wiped_existing ? `${d.wiped_existing} sostituiti, ` : ""}${d.inserted} inseriti, ${d.updated || 0} aggiornati`);
    } else { const e = await r.json().catch(() => ({})); toast.error(e.detail || "Import fallita"); }
  };

  const seedDistributoriWoo = async () => {
    setSeedingWoo(true);
    const token = localStorage.getItem("token");
    const r = await fetch(`${API}/crm/distributori/seed-woocommerce`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
    setSeedingWoo(false);
    if (r.ok) {
      const d = await r.json();
      setSeedResult(d);
      toast.success(`Distributori sincronizzati: ${d.created} creati, ${d.updated} aggiornati (totale: ${d.total_distributori_now})`);
    } else {
      const e = await r.json().catch(() => ({}));
      toast.error(e.detail || "Errore seed distributori");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-amber-50 p-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <Link to="/admin/crm" className="text-[#7A4856] hover:underline text-sm"><ArrowLeft className="w-4 h-4 inline" /> Dashboard</Link>
          <h1 className="text-2xl font-bold text-[#5A3A3A]">Import Excel</h1>
        </div>

        <div className="bg-white rounded-2xl border border-rose-100 p-6">
          <div className="flex gap-2 mb-4">
            {["clienti", "ordini", "elenco-ddt"].map((t) => (
              <button key={t} onClick={() => { setTab(t); setResult(null); setFile(null); }} data-testid={`import-tab-${t}`}
                className={`px-4 py-2 rounded-full text-sm font-medium ${tab === t ? "bg-[#B87A8A] text-white" : "bg-white border border-rose-200 text-[#7A4856]"}`}>
                {t === "clienti" ? "Anagrafiche Clienti" : t === "ordini" ? "Storico Ordini / Fatture" : "📋 Elenco DDT ITA318 (auto)"}
              </button>
            ))}
          </div>

          <div className="p-4 bg-rose-50 rounded-xl mb-4 text-sm text-gray-700">
            <p className="font-semibold mb-2">Colonne riconosciute:</p>
            {tab === "clienti" ? (
              <p className="text-xs">ragione_sociale, codice_cliente, indirizzo, cap, citta, provincia, regione, telefono, cellulare, email, pec, partita_iva, codice_fiscale, agente_email, tipo, note</p>
            ) : tab === "ordini" ? (
              <p className="text-xs">numero_ordine, data_ordine, cliente (ragione sociale), codice_cliente, importo_totale, agente_email</p>
            ) : (
              <div className="text-xs">
                <p className="font-semibold text-[#7A4856]">Formato Excel ITA318 «Elenco DDT e Fatture»:</p>
                <p>Foglio «Elenco Ft» con colonne: N Fattura, Data Fattura, N.Ordine, Data Ordine, Totale Fattura, <b>Agente</b>, <b>Destinatario</b>, Causale, Incassati.</p>
                <p className="mt-1">Crea automaticamente in un{"'"}unica passata: <b>agenti</b> (email auto-generata <code>nome@ita318.it</code>) + <b>clienti</b> + <b>ordini</b> (con prodotti dal foglio «Elenco per espos» se presente).</p>
                <p className="mt-1 text-green-700">✅ È il file che mi hai mandato — usa questo tab.</p>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-2">File supportati: .xlsx, .xls, .csv. Nessun limite di righe.</p>
          </div>

          <input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setFile(e.target.files[0])}
            data-testid="import-file-input" className="block w-full mb-4 text-sm" />

          {tab === "elenco-ddt" && (
            <label className="flex items-center gap-2 mb-3 p-3 bg-rose-50 rounded-lg border border-rose-200 cursor-pointer">
              <input type="checkbox" checked={wipeExisting} onChange={(e) => setWipeExisting(e.target.checked)} data-testid="wipe-existing-cb" className="w-4 h-4 accent-rose-600" />
              <span className="text-sm text-[#5A3A3A]">🔁 <b>Sostituisci</b> gli ordini importati in precedenza (mantiene clienti/agenti, cancella e re-importa ordini con fonte <code>import_elenco_ddt_ita318</code>)</span>
            </label>
          )}

          <button onClick={upload} disabled={!file || uploading} data-testid="btn-import-upload"
            className="px-6 py-3 bg-[#B87A8A] text-white rounded-full font-medium hover:bg-[#a06978] disabled:opacity-50">
            {uploading ? <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> : <Upload className="w-4 h-4 inline mr-2" />}
            {wipeExisting && tab === "elenco-ddt" ? "🔁 Sostituisci e importa" : "Carica e importa"}
          </button>

          {result && (
            <div className="mt-6 p-4 bg-green-50 rounded-xl text-sm" data-testid="import-result">
              {tab === "elenco-ddt" ? (
                <>
                  <p className="font-bold text-[#7A4856] mb-2">📊 Import Elenco DDT ITA318 — Riepilogo</p>
                  {result.wiped_existing > 0 && <p className="text-rose-700">🔁 <b>{result.wiped_existing}</b> ordini precedenti cancellati</p>}
                  <p>👤 <b>{result.agenti_creati}</b> agenti creati (<span className="text-gray-500">{result.agenti_esistenti} già esistenti</span>)</p>
                  <p>🏢 <b>{result.clienti_creati}</b> clienti creati · <b>{result.clienti_aggiornati}</b> aggiornati</p>
                  <p>📦 <b>{result.ordini_inseriti}</b> ordini importati (<span className="text-gray-500">{result.ordini_saltati_duplicati} duplicati saltati</span>)</p>
                  <p className="mt-2 text-xs text-gray-600">Totale righe processate: <b>{result.totale_righe}</b></p>
                  <p className="mt-3 text-xs">→ Vai su <Link to="/admin/crm" className="text-[#B87A8A] underline">CRM Dashboard</Link> per vedere il risultato.</p>
                </>
              ) : (
                <>
                  <p>✅ <b>{result.inserted}</b> nuovi inseriti</p>
                  {result.updated > 0 && <p>🔄 <b>{result.updated}</b> aggiornati</p>}
                  <p>⏭ <b>{result.skipped}</b> saltati</p>
                  <p>📊 Totale righe processate: <b>{result.total_rows}</b></p>
                </>
              )}
              {result.errors && result.errors.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-red-600">Errori ({result.errors.length})</summary>
                  <div className="text-xs mt-2 max-h-40 overflow-y-auto">
                    {result.errors.map((e, i) => <div key={i}>Riga {e.row}: {e.error}</div>)}
                  </div>
                </details>
              )}
            </div>
          )}
        </div>

        {/* SEED DISTRIBUTORI WOOCOMMERCE */}
        <div className="bg-white rounded-2xl border border-amber-200 p-6 mt-6">
          <h2 className="text-lg font-bold text-[#5A3A3A] mb-2">🏪 Seed Distributori WooCommerce</h2>
          <p className="text-sm text-gray-600 mb-3">
            Crea o aggiorna i <b>7 distributori noti</b> presenti su WooCommerce (Romina Manara #90, HT Group #87, Molinaro Aesthetics #91, La Maison de la Beauté #101, Professional Beauty Line #10, Dermatologique #57, Paolo Velo &amp; You #21). Idempotente — può essere cliccato più volte senza creare duplicati.
          </p>
          <button onClick={seedDistributoriWoo} disabled={seedingWoo} data-testid="btn-seed-distributori-woo"
            className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full font-medium hover:opacity-90 disabled:opacity-50">
            {seedingWoo ? <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> : "🏪 "}
            Sincronizza Distributori WooCommerce
          </button>
          {seedResult && (
            <div className="mt-4 p-3 bg-amber-50 rounded-xl text-sm" data-testid="seed-result">
              <p>✨ <b>{seedResult.created}</b> creati · 🔄 <b>{seedResult.updated}</b> aggiornati · 🏪 totale distributori CRM: <b>{seedResult.total_distributori_now}</b></p>
              {seedResult.details && (
                <ul className="text-xs mt-2 space-y-0.5">
                  {seedResult.details.map((d, i) => (
                    <li key={i}>#{d.wc_id} {d.rs} — <span className={d.action === "created" ? "text-green-700" : "text-blue-700"}>{d.action}</span></li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ============================================================
// OUTLOOK CONFIG
// ============================================================
export function CrmOutlookConfigPage() {
  const [cfg, setCfg] = useState({ host: "imap.gmail.com", port: 993, username: "", password: "", folder: "INBOX", sync_enabled: true });
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    (async () => {
      const r = await fetchAuth("/crm/outlook/config");
      if (r.ok) {
        const d = await r.json();
        if (d.configured) setCfg({ ...cfg, ...d, password: "" });
      }
      setLoading(false);
    })();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const applyPreset = (provider) => {
    if (provider === "gmail") {
      setCfg({ ...cfg, host: "imap.gmail.com", port: 993, folder: "INBOX" });
      toast.success("Preset Gmail applicato. Inserisci email Gmail e App Password.");
    } else if (provider === "outlook") {
      setCfg({ ...cfg, host: "outlook.office365.com", port: 993, folder: "INBOX" });
      toast.success("Preset Outlook applicato.");
    }
  };

  const save = async () => {
    if (!cfg.password) { toast.error("Password obbligatoria"); return; }
    // Rimuove spazi dalla password (Google App Password viene fornita con spazi che possono dare problemi)
    const cleanPwd = (cfg.password || "").replace(/\s+/g, "");
    const r = await fetchAuth("/crm/outlook/config", { method: "POST", body: JSON.stringify({ ...cfg, password: cleanPwd }) });
    if (r.ok) { toast.success("Configurazione salvata. Premi 'Test connessione' per verificare."); setCfg({ ...cfg, password: "", password_set: true }); }
    else toast.error("Errore salvataggio");
  };

  const testConnection = async () => {
    setTesting(true);
    const r = await fetchAuth("/crm/outlook/test", { method: "POST" });
    setTesting(false);
    if (r.ok) { const d = await r.json(); d.success ? toast.success(d.message) : toast.error(d.message); }
    else toast.error("Test fallito");
  };

  const forceSync = async () => {
    setTesting(true);
    const r = await fetchAuth("/crm/outlook/sync?mode=ALL&limit=50", { method: "POST" });
    setTesting(false);
    if (r.ok) {
      const d = await r.json();
      toast.success(`Sync ALL completato — Inseriti: ${d.inserted}, Duplicati: ${d.skipped_duplicates}, Ignorati: ${d.ignored_non_orders}`);
    } else {
      toast.error("Sync fallito");
    }
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin text-rose-400" /></div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-amber-50 p-6">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <Link to="/admin/crm" className="text-[#7A4856] hover:underline text-sm"><ArrowLeft className="w-4 h-4 inline" /> Dashboard</Link>
          <h1 className="text-2xl font-bold text-[#5A3A3A]">Configurazione IMAP — Email Ordini</h1>
        </div>

        <div className="bg-white rounded-2xl border border-rose-100 p-6">
          <p className="text-sm text-gray-600 mb-2">
            Configura una casella IMAP per importare automaticamente gli ordini WooCommerce dal sito ITA318.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 mb-4">
            <b>⚠️ Microsoft 365 ha bloccato il login Basic Auth IMAP.</b> Soluzione: configura un <b>inoltro Gmail</b> (da Outlook → Gmail) e usa Gmail come casella IMAP.<br/>
            <b>Configurazione consigliata:</b> Gmail (<code>odr.ordini@gmail.com</code>) con <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="underline text-amber-800">App Password Google</a>.
          </div>

          <div className="flex gap-2 mb-4">
            <button type="button" onClick={() => applyPreset("gmail")} data-testid="preset-gmail" className="px-3 py-1.5 text-xs rounded-full bg-rose-50 border border-rose-200 text-[#7A4856] hover:bg-rose-100">
              📧 Preset Gmail
            </button>
            <button type="button" onClick={() => applyPreset("outlook")} data-testid="preset-outlook" className="px-3 py-1.5 text-xs rounded-full bg-rose-50 border border-rose-200 text-[#7A4856] hover:bg-rose-100">
              📨 Preset Outlook
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs text-gray-500">Username (email)</label>
              <input type="email" value={cfg.username} onChange={(e) => setCfg({ ...cfg, username: e.target.value })}
                data-testid="outlook-username" placeholder="odr.ordini@gmail.com" className="w-full px-3 py-2 border border-rose-200 rounded-lg text-sm" />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-gray-500">Password (App Password — gli spazi vengono rimossi automaticamente)</label>
              <input type="password" value={cfg.password} onChange={(e) => setCfg({ ...cfg, password: e.target.value })}
                data-testid="outlook-password" placeholder={cfg.password_set ? "•••••••• (lascia vuoto per non cambiare)" : "es. micv kanh nobz rqko"}
                className="w-full px-3 py-2 border border-rose-200 rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Host IMAP</label>
              <input value={cfg.host} onChange={(e) => setCfg({ ...cfg, host: e.target.value })}
                data-testid="outlook-host" placeholder="imap.gmail.com" className="w-full px-3 py-2 border border-rose-200 rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Porta</label>
              <input type="number" value={cfg.port} onChange={(e) => setCfg({ ...cfg, port: parseInt(e.target.value) })}
                data-testid="outlook-port" className="w-full px-3 py-2 border border-rose-200 rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Cartella</label>
              <input value={cfg.folder} onChange={(e) => setCfg({ ...cfg, folder: e.target.value })}
                data-testid="outlook-folder" className="w-full px-3 py-2 border border-rose-200 rounded-lg text-sm" />
            </div>
            <div className="flex items-center gap-2 mt-6">
              <input type="checkbox" checked={cfg.sync_enabled} onChange={(e) => setCfg({ ...cfg, sync_enabled: e.target.checked })} data-testid="outlook-sync-enabled" />
              <label className="text-sm">Sync attivo (ogni 5 min)</label>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 mt-4">
            ℹ️ <b>Importante:</b> il pulsante &quot;Test connessione&quot; verifica la configurazione <b>salvata nel database</b>. Premi sempre <b>Salva</b> prima di &quot;Test connessione&quot;.
          </div>

          <div className="flex gap-2 mt-4 flex-wrap">
            <button onClick={save} className="px-4 py-2 bg-[#B87A8A] text-white rounded-full text-sm font-medium hover:bg-[#a06978]" data-testid="btn-outlook-save">
              <Settings className="w-4 h-4 inline mr-1" /> Salva
            </button>
            <button onClick={testConnection} disabled={testing} className="px-4 py-2 bg-white border border-rose-200 text-[#7A4856] rounded-full text-sm font-medium hover:bg-rose-50" data-testid="btn-outlook-test">
              {testing ? <Loader2 className="w-4 h-4 inline animate-spin mr-1" /> : <Eye className="w-4 h-4 inline mr-1" />}
              Test connessione
            </button>
            <button onClick={forceSync} disabled={testing} className="px-4 py-2 bg-emerald-100 border border-emerald-300 text-emerald-800 rounded-full text-sm font-medium hover:bg-emerald-200" data-testid="btn-outlook-force-sync">
              {testing ? <Loader2 className="w-4 h-4 inline animate-spin mr-1" /> : "🔄"} Forza sync (recupera anche email già lette)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
